<template>
  <div
    v-if="visible"
    class="gm-billing-overlay"
    role="presentation"
    @mousedown.self="close"
  >
    <section
      ref="dialog"
      class="gm-billing-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gm-billing-title"
      aria-describedby="gm-billing-description"
      tabindex="-1"
    >
      <header class="gm-billing-header">
        <div class="gm-billing-header__icon" aria-hidden="true">
          <i class="fa-regular fa-calendar-check"></i>
        </div>
        <div class="gm-billing-header__copy">
          <div class="gm-billing-header__eyebrow">ADMINISTRACIÓN DE CONTRATOS</div>
          <h2 id="gm-billing-title">Configuración de facturación</h2>
          <div class="gm-billing-header__meta">
            <span class="gm-billing-contract">Contrato #{{ contractId || '—' }}</span>
            <span class="gm-billing-meta-separator" aria-hidden="true"></span>
            <span>Calendario de periodos de cobro</span>
          </div>
        </div>
        <button
          ref="closeButton"
          class="gm-billing-icon-button"
          type="button"
          aria-label="Cerrar configuración de facturación"
          title="Cerrar"
          @click="close"
        >
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          <span class="gm-sr-only">Cerrar</span>
        </button>
      </header>

      <main class="gm-billing-content">
        <div class="gm-billing-notice" role="note">
          <div class="gm-billing-notice__icon" aria-hidden="true">
            <i class="fa-solid fa-circle-info"></i>
          </div>
          <div class="gm-billing-notice__text">
            <strong>Antes de cambiar el calendario</strong>
            <p id="gm-billing-description">
              Simula los nuevos periodos y revisa las fechas antes de confirmar.
              Los cambios no modifican la fecha de inicio del contrato ni los adeudos ya registrados.
            </p>
          </div>
        </div>

        <div class="gm-billing-panel">
          <div class="gm-billing-panel__heading">
            <div>
              <span class="gm-billing-panel__step">01 · CONFIGURACIÓN</span>
              <h3>Fecha y periodos de facturación</h3>
              <p>Consulta el calendario actual, simula el cambio y confirma cuando corresponda.</p>
            </div>
          </div>

          <ContractBillingDay
            v-if="contractId"
            :key="contractId"
            :contract-id="contractId"
            :can-edit="canEdit"
            @billing-updated="onBillingUpdated"
          />
          <div v-else class="gm-billing-empty">
            <i class="fa-regular fa-folder-open" aria-hidden="true"></i>
            <p>Selecciona un contrato para consultar su calendario de facturación.</p>
          </div>
        </div>
      </main>

      <footer class="gm-billing-footer">
        <span class="gm-billing-footer__hint">
          <i class="fa-solid fa-shield-halved" aria-hidden="true"></i>
          Los cambios requieren confirmación.
        </span>
        <button class="gm-billing-close-button" type="button" @click="close">
          Cerrar ventana
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import ContractBillingDay from '@/components/contracts/ContractBillingDay.vue';

export default {
  name: 'ModalContractBillingDay',
  components: { ContractBillingDay },
  props: {
    visible: { type: Boolean, default: false },
    contractId: { type: [Number, String], default: null },
    canEdit: { type: Boolean, default: false }
  },
  data() {
    return { previousFocusedElement: null, previousBodyOverflow: null };
  },
  watch: {
    visible: {
      immediate: true,
      handler(isVisible) {
        if (typeof document === 'undefined') return;
        if (isVisible) {
          this.$nextTick(() => this.activateModal());
        } else {
          this.deactivateModal();
        }
      }
    }
  },
  beforeDestroy() {
    this.deactivateModal();
  },
  methods: {
    close() {
      this.$emit('close');
    },
    onBillingUpdated(event) {
      this.$emit('billing-updated', event);
    },
    activateModal() {
      if (!this.visible || typeof document === 'undefined') return;
      if (this.previousBodyOverflow === null) {
        this.previousFocusedElement = document.activeElement;
        this.previousBodyOverflow = document.body.style.overflow;
      }
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);
      if (this.$refs.closeButton) this.$refs.closeButton.focus();
    },
    deactivateModal() {
      if (typeof document === 'undefined') return;
      document.removeEventListener('keydown', this.onKeydown);
      if (this.previousBodyOverflow !== null) {
        document.body.style.overflow = this.previousBodyOverflow;
        this.previousBodyOverflow = null;
      }
      if (this.previousFocusedElement && typeof this.previousFocusedElement.focus === 'function') {
        this.previousFocusedElement.focus();
      }
      this.previousFocusedElement = null;
    },
    onKeydown(event) {
      if (!this.visible) return;
      if (event.key === 'Escape') {
        // No cerrar mientras SweetAlert2 esté mostrando una confirmación.
        if (document.querySelector('.swal2-container')) return;
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.$refs.dialog) return;
      // Mantener el foco dentro del diálogo principal.
      // SweetAlert2 administra su propio foco cuando está abierto.
      if (document.querySelector('.swal2-container')) return;
      const nodes = this.$refs.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const focusables = Array.prototype.filter.call(nodes, node => node.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
};
</script>

<style scoped>
.gm-billing-overlay {
  position: fixed;
  inset: 0;
  z-index: 2050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, .66);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}
.gm-billing-dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 980px;
  max-height: min(92dvh, 980px);
  overflow: hidden;
  background: #f8fafc;
  border: 1px solid rgba(226, 232, 240, .85);
  border-radius: 20px;
  box-shadow: 0 28px 90px rgba(2, 8, 23, .34);
  animation: gm-billing-enter .2s ease-out both;
}
.gm-billing-dialog:focus { outline: none; }
.gm-billing-header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 0 0 auto;
  padding: 24px 30px;
  background: #fff;
  border-bottom: 1px solid #e9eef5;
}
.gm-billing-header__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 15px;
  background: #eaf1ff;
  color: #1d4ed8;
  font-size: 22px;
}
.gm-billing-header__copy { min-width: 0; flex: 1; }
.gm-billing-header__eyebrow {
  margin-bottom: 5px;
  color: #64748b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .13em;
}
.gm-billing-header h2 {
  margin: 0;
  color: #0f172a;
  font-size: clamp(19px, 2vw, 24px);
  font-weight: 750;
  letter-spacing: -.025em;
  line-height: 1.25;
}
.gm-billing-header__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 7px;
  color: #64748b;
  font-size: 12px;
}
.gm-billing-contract { color: #334155; font-weight: 700; }
.gm-billing-meta-separator { width: 4px; height: 4px; border-radius: 50%; background: #94a3b8; }
.gm-billing-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 37px;
  height: 37px;
  flex-shrink: 0;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #64748b;
  font-size: 18px;
  cursor: pointer;
  transition: background .15s, color .15s, border-color .15s;
}
.gm-billing-icon-button:hover { color: #0f172a; background: #f1f5f9; border-color: #cbd5e1; }
.gm-billing-icon-button:focus-visible, .gm-billing-close-button:focus-visible {
  outline: 3px solid #93c5fd;
  outline-offset: 2px;
}
.gm-billing-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 24px 30px 30px;
}
.gm-billing-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 15px 17px;
  margin-bottom: 22px;
  color: #334155;
  background: #eff6ff;
  border: 1px solid #cfe1fb;
  border-radius: 12px;
}
.gm-billing-notice__icon { margin-top: 1px; flex: 0 0 auto; color: #2563eb; font-size: 17px; }
.gm-billing-notice__text { min-width: 0; }
.gm-billing-notice strong { display: block; margin-bottom: 3px; font-size: 13px; color: #1e3a8a; }
.gm-billing-notice p { margin: 0; font-size: 12px; line-height: 1.6; }
.gm-billing-panel {
  padding: 23px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 15px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, .025);
}
.gm-billing-panel__heading { padding-bottom: 15px; margin-bottom: 19px; border-bottom: 1px solid #edf1f5; }
.gm-billing-panel__step { display: block; margin-bottom: 6px; color: #2563eb; font-size: 10px; font-weight: 800; letter-spacing: .12em; }
.gm-billing-panel__heading h3 { margin: 0 0 5px; color: #0f172a; font-size: 17px; font-weight: 750; }
.gm-billing-panel__heading p { margin: 0; color: #64748b; font-size: 12px; line-height: 1.6; }
.gm-billing-empty { text-align: center; padding: 34px 16px; color: #64748b; }
.gm-billing-empty i { font-size: 24px; margin-bottom: 10px; }
.gm-billing-empty p { margin: 0; font-size: 13px; }
.gm-billing-footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 30px;
  background: #fff;
  border-top: 1px solid #e9eef5;
}
.gm-billing-footer__hint { display: inline-flex; align-items: center; gap: 8px; color: #94a3b8; font-size: 11px; }
.gm-billing-footer__hint i { font-size: 12px; }
.gm-billing-close-button {
  padding: 9px 20px;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 9px;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s, border-color .15s;
}
.gm-billing-close-button:hover { background: #f1f5f9; border-color: #94a3b8; }
.gm-sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
@keyframes gm-billing-enter { from { opacity: 0; transform: translateY(10px) scale(.985); } to { opacity: 1; transform: translateY(0) scale(1); } }
@media (max-width: 767px) {
  .gm-billing-overlay { padding: 10px; align-items: center; }
  .gm-billing-dialog { max-height: calc(100dvh - 20px); border-radius: 15px; }
  .gm-billing-header { padding: 16px; gap: 12px; }
  .gm-billing-header__icon { width: 42px; height: 42px; font-size: 18px; border-radius: 12px; }
  .gm-billing-header__eyebrow { font-size: 9px; }
  .gm-billing-header h2 { font-size: 18px; }
  .gm-billing-header__meta { gap: 7px; font-size: 11px; }
  .gm-billing-content { padding: 16px; }
  .gm-billing-notice { padding: 12px; margin-bottom: 15px; }
  .gm-billing-panel { padding: 15px; }
  .gm-billing-footer { padding: 12px 16px; }
  .gm-billing-footer__hint { display: none; }
  .gm-billing-close-button { width: 100%; }
}
@media (max-width: 420px) {
  .gm-billing-header__icon { display: none; }
  .gm-billing-header h2 { font-size: 17px; }
  .gm-billing-header__meta { font-size: 10px; }
  .gm-billing-panel { padding: 12px; }
}
@media (prefers-reduced-motion: reduce) {
  .gm-billing-dialog { animation: none; }
  .gm-billing-icon-button, .gm-billing-close-button { transition: none; }
}
</style>
